import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { Typography, TextField, Select, MenuItem, FormControl } from "@material-ui/core";
import SelectItemWizard from "./SelectItemWizard";
import { LinearProgress } from "@material-ui/core";
import Lottie from "lottie-react";
import aiLoadingAnimation from "../../assets/animations/loading-anim.json";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Tooltip,
} from "@material-ui/core";
import DirectionsBikeIcon from "@material-ui/icons/DirectionsBike";
import DateRangeIcon from "@material-ui/icons/DateRange";
import RoomIcon from "@material-ui/icons/Room";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SearchIcon from "@material-ui/icons/Search";
import ErrorIcon from "@material-ui/icons/Error";
import BlockIcon from "@material-ui/icons/Block";
import SuggestedTireCard from "./SuggestedTireCard";
import { Autocomplete } from "@material-ui/lab";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// Validation functions
function validateFullName(name) {
  const nameParts = name.trim().split(/\s+/);
  return nameParts.length >= 2 && nameParts.every(part => part.length > 0);
}

function validateIsraeliPhone(phone) {
  // Clean phone number by removing spaces, dashes, and other non-digit characters except leading digits
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  const phoneRegex = /^05\d{8}$/;
  return phoneRegex.test(cleanPhone);
}

// Helper function to clean phone input
function cleanPhoneInput(phone) {
  return phone.replace(/[\s\-\(\)]/g, '');
}

const styles = (theme) => ({
  paper: {
    margin: "80px auto",
    width: "65%",
    padding: "2rem",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    '@media (max-width: 1050px)': {
      width: "90%", 
    },
    '@media (max-width: 1024px)': {
      margin: "32px auto", 
    },
    '@media (max-width: 460px)': {
      width: "calc(100% - 24px)",
      padding: "1.25rem",
    },
  },
  containerLayout: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },
  columnLayoutForm: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "34rem",
    '@media (max-width: 870px)': {
      width: "100%",
    },
    '@media (max-width: 470px)': {
      width: "100%",
    },
    '@media (max-width: 370px)': {
      width: "100%",
    },
  },
  columnLayoutResult: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "48rem",
    '@media (max-width: 870px)': {
      width: "100%",   
    },
    '@media (max-width: 480px)': {
      width: "100%",
    },
    '@media (max-width: 370px)': {
      width: "100%",
    },
  },
  columnTitle: {
    fontSize: "30px",
    fontWeight: "bold",
    overflowWrap: "break-word",
    '@media (max-width: 480px)': {
      fontSize: "1.55rem",
      lineHeight: 1.25,
    },
  },
  columnSubTitle: {
    fontSize: "20px",
    fontWeight: "525",
    color: "#A2A2A2",
    width: "100%",
    overflowWrap: "break-word",
    '@media (max-width: 480px)': {
      fontSize: "0.95rem",
      lineHeight: 1.45,
    },
    '@media (max-width: 370px)': {
      fontSize: "15px",   
    },
  },
  containerSuggestedTire: {
    width: "100%", 
    position: "relative", 
    display: "flex", 
    flexDirection: "column"
  },
  containerSelects: {
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
    marginTop: "1rem",
  },
  formControl: {
    margin: theme.spacing() / 2,
    width: "100%",
    boxSizing: "border-box",
    '@media (max-width: 480px)': {
      margin: "4px 0",
    },
  },
  textField: {
    width: "100%",
  },
  containerPickMoto: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },
  textTitleForm: {
    fontSize: "18px",
    fontWeight: "550",
    marginBottom: "0.2rem",
  },
  formKilometers: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F1F1F1",
      borderRadius: 6,
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      height: "2.4rem",
      padding: "0.1rem 0.6rem 0.1rem 0.6rem",
      display: "flex",
      alignItems: "center",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
      "& input": {
        border: "none",
        boxSizing: "content-box",
        fontSize: "1rem",          
        fontFamily: "inherit",
        "&::placeholder": {
          color: "#A2A2A2",
          opacity: 1,
        },
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "1rem",
      fontWeight: "500",
      color: "#333",
      backgroundColor: "#fff",
      padding: "0 4px",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
        backgroundColor: "#fff",
      },
    },
    // Remove focus shadow for Select components
    "& .MuiSelect-root": {
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiSelect-select": {
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    // Remove focus styling from Select
    "& .MuiSelect-select.MuiSelect-select": {
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    // Remove focus ring and shadow
    "& .MuiSelect-root.Mui-focused": {
      "& .MuiSelect-select": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiSelect-select.Mui-focused": {
      backgroundColor: "transparent",
    },
  },
  containerProgress: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    marginTop: "0.5rem",
  },
  containerTextProgress: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  rootProgress: {
    position: "relative",
    width: "100%",
    height: "0.6rem",
    borderRadius: 6,
    backgroundColor: "#F1F1F1",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  },
  barProgress: {
    borderRadius: 6,
    background: "#FF7500",
  },
  containerTableSummary: {
    display: "flex",
    justifyContent: "center",
    marginTop: "2rem",
  },
  rootTable: {
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #FF7500",
  },
  header: {
    backgroundColor: "#FF7500",
    color: "#fff",
    padding: "0.2rem 1rem",
    fontWeight: "bold",
    textAlign: "left",
  },
  table: {
    minWidth: 300,
    borderCollapse: "collapse",
    borderRadius: 8,
    "& td": {
      padding: "0.8rem 1rem",
    },
    "& tr:nth-child(1) td, & tr:nth-child(2) td": {
      borderBottom: "1px solid #FF7500",
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
    "& td:nth-child(1)": {
      fontWeight: "bold",
    },
    "& td:nth-child(2)": {
      borderLeft: "1px solid #FF7500",
    },
  },
  iconCell: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 500,
  },
  containerButtonGenerate: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "1rem",
  },
  tooltipContent: {
    fontSize: "12px", 
  },
  containerNoMotoAvailable: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  buttonNoMotoAvailable: {
    fontSize: "14px",
    width: "75%",
    color: "#ff7900",
    textDecoration: "underline",
    marginTop: "0.5rem",
    textTransform: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      boxShadow: "none",
    },
    '@media (max-width: 370px)': {
      width: "100%",
    },
  },
  containerButtonGenerateBack:{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    marginTop: "2rem",
    width: "100%",
    gap: "1rem",
    '@media (max-width: 370px)': {
      flexDirection: "column",
      gap: "0",
    },
  },
  buttonContinue: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "100%",
    height: "3rem",
    padding: "0 2rem",
    // marginTop: "1rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
    '@media (max-width: 370px)': {
      marginTop: "0.6rem",
    },
  },
  buttonGenerate: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "100%",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
    '@media (max-width: 370px)': {
      marginTop: "0.6rem",
    },
  },
  buttonStepBack: {
    backgroundColor: "#e1e1e1",
    color: "#000",
    width: "50%",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#adadad",
      boxShadow: "inset 0 4px 6px rgba(0, 0, 0, 0.2)",
    },
    '@media (max-width: 370px)': {
      width: "100%",
    },
  },
  buttonGenerateDisabled: {
    backgroundColor: "#F1F1F1",
    color: "#000",
    width: "100%",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    boxShadow: "inset 0 4px 6px rgba(0, 0, 0, 0.2)",
    '@media (max-width: 370px)': {
      marginTop: "0.6rem",
    },
  },

  containerLoader: {
    marginTop: "2rem",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  loaderSubTitle: {
    fontSize: "14px",
    color: "#A2A2A2",
    marginTop: "0.5rem",
  },
  containerBackButton: {
    marginTop: "2rem",
  },

  lottieContainer: {
    width: "12rem",
    height: "12rem",
    margin: "2rem auto",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  inlineLoadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 0",
  },
  loadingText: {
    fontSize: "16px",
    color: "#666",
    fontWeight: "525",
    marginTop: "1rem",
    textAlign: "center",
    '@media (max-width: 470px)': {
      maxWidth: "20rem",   
    },
  },

  successPanel: {
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: "10000",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  successPanelContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "2rem",
    maxWidth: "400px",
    width: "90%",
    textAlign: "center",
    boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
    '@media (max-width: 520px)': {
      maxWidth: "18rem",  
    },
    '@media (max-width: 370px)': {
      maxWidth: "12rem",  
    },
  },
  successIcon: {
    fontSize: "48px",
    color: "#4CAF50",
    marginBottom: "1rem",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "1rem",
  },
  successMessage: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "2rem",
    lineHeight: "1.4",
  },
  successButton: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "100%",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    border: "none",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
  },
  noTiresFoundContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "2rem",
    padding: "2rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    border: "1px solid #e1e1e1",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
  },
  noTiresFoundIcon: {
    fontSize: "48px",
    color: "#A2A2A2",
    marginBottom: "1rem",
  },
  noTiresFoundTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "1rem",
    '@media (max-width: 480px)': {
      fontSize: "20px",
    },
  },
  noTiresFoundMessage: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "2rem",
    lineHeight: "1.5",
    maxWidth: "400px",
    '@media (max-width: 480px)': {
      fontSize: "14px",
      maxWidth: "300px",
    },
  },
  noTiresFoundButton: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "200px",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
    '@media (max-width: 480px)': {
      width: "100%",
    },
  },
  gptErrorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "2rem",
    padding: "2rem",
    backgroundColor: "#fff5f5",
    borderRadius: "12px",
    border: "1px solid #fecaca",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
  },
  gptErrorIcon: {
    fontSize: "48px",
    color: "#ef4444",
    marginBottom: "1rem",
  },
  gptErrorTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#dc2626",
    marginBottom: "1rem",
    '@media (max-width: 480px)': {
      fontSize: "20px",
    },
  },
  gptErrorMessage: {
    fontSize: "16px",
    color: "#991b1b",
    marginBottom: "2rem",
    lineHeight: "1.5",
    maxWidth: "400px",
    '@media (max-width: 480px)': {
      fontSize: "14px",
      maxWidth: "300px",
    },
  },
  gptErrorButton: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "200px",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
    '@media (max-width: 480px)': {
      width: "100%",
    },
  },
  limitReachedContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "2rem",
    padding: "2rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    border: "1px solid #e1e1e1",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
  },
  limitReachedIcon: {
    fontSize: "48px",
    color: "#A2A2A2",
    marginBottom: "1rem",
  },
  limitReachedTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "1rem",
    '@media (max-width: 480px)': {
      fontSize: "20px",
    },
  },
  limitReachedMessage: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "2rem",
    lineHeight: "1.5",
    maxWidth: "400px",
    '@media (max-width: 480px)': {
      fontSize: "14px",
      maxWidth: "300px",
    },
  },
  limitReachedButton: {
    backgroundColor: "#ff7900",
    color: "#fff",
    width: "200px",
    height: "3rem",
    padding: "0 2rem",
    borderRadius: "6px",
    textTransform: "uppercase",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#e66f00",
    },
    '@media (max-width: 480px)': {
      width: "100%",
    },
  },
  autocompleteListbox: {
    // Custom scrollbar styling for autocomplete dropdown
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#E0E0E0",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#9f9f9f",
      borderRadius: "4px",
      border: "1px solid #E0E0E0",
      "&:hover": {
        backgroundColor: "#7f7f7f",
      },
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "#E0E0E0",
    },
    // Firefox scrollbar styling
    scrollbarWidth: "thin",
    scrollbarColor: "#9f9f9f #E0E0E0",
    maxHeight: "200px",
  },
  autocompletePaper: {
    backgroundColor: "#F1F1F1",
    borderRadius: "6px",
    marginTop: "0.25rem",
  },
  // Global Select focus removal
  selectFocusRemoval: {
    "& .MuiSelect-root": {
      "&:focus": {
        backgroundColor: "transparent !important",
        boxShadow: "none !important",
      },
      "&.Mui-focused": {
        backgroundColor: "transparent !important",
        boxShadow: "none !important",
      },
    },
    "& .MuiSelect-select": {
      "&:focus": {
        backgroundColor: "transparent !important",
        boxShadow: "none !important",
      },
      "&.Mui-focused": {
        backgroundColor: "transparent !important",
        boxShadow: "none !important",
      },
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused": {
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25) !important",
      },
    },
  },
});

const hebrewLables = {
  // Main titles
  titleFirstSection: "מצא את הצמיגים הטובים ביותר",
  titleSecondSection: "עבורך!",
  subtitleFirstSection: "ענה על כמה שאלות כדי לקבל ",
  subtitleSecondSection: "את ההמלצות הטובות ביותר לצמיגים עבורך",
  
  // Form field labels
  insertYourName: "הכנס את שמך",
  insertYourPhoneNumber: "הכנס את מספר הטלפון שלך",
  pickYourMotorcycleBrand: "בחר את מותג האופנוע שלך",
  selectYourMotorcycleYear: "בחר את שנת האופנוע שלך",
  doYouWantFrontRearOrSet: "האם אתה רוצה צמיג קדמי, אחורי או סט?",
  insertYourMotorcycleModelInfo: "הכנס את פרטי דגם האופנוע שלך",
  pickYourMotorcycle: "בחר את האופנוע שלך",
  insertYourMotorcycleModel: "הכנס את דגם האופנוע שלך",
  howDoYouRide: "איך אתה רוכב?",
  yourMonthlyKilometers: "הקילומטרים החודשיים שלך",
  completedQuestions: "שאלות שהושלמו",
  
  // Placeholders
  name: "שם",
  phoneNumber: "מספר טלפון",
  enterYourMotorcycleModel: "הכנס את דגם האופנוע שלך",
  
  // Default options
  selectYourMotorcycleBrand: "בחר את מותג האופנוע שלך",
  selectYourMotorcycleYear: "בחר את שנת האופנוע שלך",
  chooseFrontRearOrSet: "בחר קדמי, אחורי או סט",
  selectYourUsage: "בחר את השימוש שלך",
  averageKilometers: "קילומטרים ממוצעים",
  
  // Summary table
  summary: "סיכום",
  brand: "מותג",
  year: "שנה",
  bike: "אופנוע",
  usage: "שימוש",
  km: "ק\"מ",
  
  // Custom bike model message
  yourBikeModelNotInCatalog: "דגם האופנוע שלך לא נמצא בקטלוג שלנו! לחץ כאן והודע לנו.",
  
  // Buttons
  continue: "המשך",
  back: "חזור",
  sendUs: "שלח לנו",
  generate: "צור",
  tryAgain: "נסה שוב",
  backToStart: "חזור להתחלה",
  
  // Tooltips
  pleaseCompleteAllQuestionsBeforeProceeding: "אנא השלם את כל השאלות לפני ההמשך",
  pleaseEnterYourMotorcycleModelBeforeSending: "אנא הכנס את דגם האופנוע שלך לפני השליחה",
  pleaseCompleteAllQuestionsBeforeGenerating: "אנא השלם את כל השאלות לפני היצירה",
  
  // Results section
  topTires: "הצמיגים הטובים ביותר לאופנוע שלך",
  recommendations: "המלצות",
  
  // Error and status messages
  noTiresFound: "לא נמצאו צמיגים",
  noTiresFoundMessage: "כרגע אין לנו צמיגים שעומדים בדרישות של דגם האופנוע שלך.",
  serviceTemporarilyUnavailable: "השירות זמנית לא זמין",
  limitReachedMessage: "הגענו למגבלה השבועית שלנו להמלצות צמיגים. אנא נסה שוב בשבוע הבא או פנה לצוות התמיכה שלנו לעזרה.",
  aiResponseError: "שגיאת תגובת בינה מלאכותית",
  errorToGenerateAnswers: "שגיאה ביצירת התשובות שלך, נסה מאוחר יותר...",
  weAreSearchingBestTires: "אנחנו מחפשים את ההמלצות הטובות ביותר לצמיגים עבורך...",
  weAreReceivingYourMotorcycleModel: "אנחנו מקבלים את דגם האופנוע שלך...",
  success: "הצלחה!",
  yourMotorcycleModelHasBeenSent: "דגם האופנוע שלך נשלח לרשימת הבדיקה שלנו, נוסיף אותו בקרוב!",
  
  // Loading and overlay messages
  searchingBestTiresRecommendation: "אנחנו מחפשים את ההמלצות הטובות ביותר לצמיגים עבורך...",
  receivingMotorcycleModel: "אנחנו מקבלים את דגם האופנוע שלך...",
  
  // Validation error messages
  errors: {
    fullNameRequired: "נא להזין שם פרטי ושם משפחה",
    phoneFormat: "נא להזין מספר נייד ישראלי בפורמט 05XXXXXXXX",
  },
};

const generateMotorcycleYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1960; year <= currentYear; year++) {
    years.push(year.toString());
  }
  return years.reverse(); // Newest to oldest
};

// Translate usage options to Hebrew
const USAGE_OPTIONS = ["יומי", "סוף שבוע", "חודשי"];
const KILOMETERS_OPTIONS = ["0-100", "100-500", "500-1000", "1000-5000", "5000+"];
const MOTORCYCLE_YEARS = generateMotorcycleYears();
// Translate tire type options to Hebrew
const TYRE_TYPE_OPTIONS = ["קדמי", "אחורי", "סט"];

class TireWizardPage extends React.Component {
  state = {
    validationErrors: {
      name: false,
      phone: false,
    }
  };

  componentDidMount() {
    this.props.actions.getAllMotorcycleBrands();
    this.loadSavedUserData();
  }

  componentDidUpdate(prevProps) {
    const { currentWizardStep } = this.props.catalog.wizardPage;
    const { currentWizardStep: prevStep } = prevProps.catalog.wizardPage;
    
    // If user returned to step 0, reload saved data
    if (currentWizardStep === 0 && prevStep !== 0) {
      this.loadSavedUserData();
    }
  }

  loadSavedUserData = () => {
    try {
      const savedName = localStorage.getItem('wizard_visitor_name');
      const savedPhone = localStorage.getItem('wizard_visitor_phone');
      
      if (savedName || savedPhone) {
        // Update Redux state with saved data
        const updates = {};
        if (savedName) {
          updates.visitorName = savedName;
        }
        if (savedPhone) {
          updates.visitorPhoneNumber = savedPhone;
        }
        this.props.actions.updateWizardUserAnswer(updates);

        // Update validation state for pre-filled data
        const validationErrors = { ...this.state.validationErrors };
        if (savedName) {
          validationErrors.name = !validateFullName(savedName);
        }
        if (savedPhone) {
          validationErrors.phone = !validateIsraeliPhone(savedPhone);
        }
        this.setState({ validationErrors });
      }
    } catch (error) {
      console.error('Error loading saved user data:', error);
    }
  };

  saveUserData = (name, phone) => {
    try {
      if (name) {
        localStorage.setItem('wizard_visitor_name', name);
      }
      if (phone) {
        localStorage.setItem('wizard_visitor_phone', phone);
      }
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  setWizardUserAnswer = (key, value) => {
    this.props.actions.updateWizardUserAnswer({ [key]: value });
  };

  handleTextFieldChange = (event) => {
    const { value, id } = event.target;
    let _value = value;

    // Real-time validation
    const validationErrors = { ...this.state.validationErrors };
    
    if (id === "visitorName") {
      validationErrors.name = _value.trim().length > 0 && !validateFullName(_value);
    } else if (id === "visitorPhoneNumber") {
      // Clean phone input and update the value
      _value = cleanPhoneInput(_value);
      validationErrors.phone = _value.trim().length > 0 && !validateIsraeliPhone(_value);
    }

    this.setState({ validationErrors });
    this.setWizardUserAnswer(id, _value);

    // Save to localStorage when user types
    if (id === "visitorName" || id === "visitorPhoneNumber") {
      const currentData = this.props.catalog.wizardPage;
      const newName = id === "visitorName" ? _value : currentData.visitorName;
      const newPhone = id === "visitorPhoneNumber" ? _value : currentData.visitorPhoneNumber;
      this.saveUserData(newName, newPhone);
    }
  };

  handleContinueClick = () => {
    const { motorcycleBrand, motorcycleYear } = this.props.catalog.wizardPage;

    this.props.actions.getMotorcyclesByUserModelAndYear(motorcycleBrand, motorcycleYear)
      .then(() => {
        const { motoListForGPT } = this.props.catalog.wizardPage;
        if (motoListForGPT && motoListForGPT.length > 0) {
          this.props.actions.setWizardStep(2); // Go to step 2 only if motorcycles found
        }
      });
  };

  handleGenerateClick = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 10);

    const { visitorName, visitorPhoneNumber, motorcycle, usage, kilometers, selectedTyreType } = this.props.catalog.wizardPage;

    if(visitorName && visitorPhoneNumber && motorcycle && usage && kilometers && selectedTyreType) {
      
      const wizardResponses = { visitorName, visitorPhoneNumber, motorcycle, usage, kilometers, selectedTyreType };
      
      this.props.actions.setWizardLoadingState();
      this.props.actions.sendAnswersToAI(wizardResponses);
    }
  };

  handleResetWizard = () => {
      this.props.actions.resetWizardAnswers();
      this.props.actions.setWizardStep(0);
  };

  renderLottieAnimation = (classes) => {
    return (
      <div className={classes.lottieContainer}>
        <Lottie 
          animationData={aiLoadingAnimation} 
          loop={true}
          autoplay={true}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            fontSize: "16px"
          }}
        />
      </div>
    );
  };

  handleShowCustomBikeInput = () => {
    this.props.actions.setWizardStep(1.5);
  };

  handleBackToStep0 = () => {
    this.props.actions.setWizardStep(0);
  };

  handleBackToStep1 = () => {
    this.props.actions.setWizardStep(1);
  };

  handleCustomBikeModelChange = (event) => {
    this.props.actions.updateWizardUserAnswer({ userBikeModelToInsert: event.target.value });
  };

  handleSendUserBikeToAdd = () => {
    const { userBikeModelToInsert } = this.props.catalog.wizardPage;
    
    if (userBikeModelToInsert && userBikeModelToInsert.trim()) {
      this.props.actions.sendUserModelToAdd(userBikeModelToInsert)
        .then((result) => {
          if (result.ok) {
            // Clear the input field
            this.props.actions.updateWizardUserAnswer({ userBikeModelToInsert: '' });
          }
        })
        .catch((error) => {
          console.error('Error submitting motorcycle model:', error);
        });
    }
  };

  handleCloseSuccessPanel = () => {
    // Hide success panel and go back to step 1
    this.props.actions.hideUserModelSuccess();
    this.props.actions.resetWizardAnswers();
    this.props.actions.setWizardStep(0);
  };

  handleTireClick = (tireInfo) => {
    // Get the current wizard responses
    const { visitorName, visitorPhoneNumber, motorcycle, usage, kilometers, selectedTyreType, motorcycleBrand, motorcycleYear, usageId } = this.props.catalog.wizardPage;
    
    const wizardResponses = {
      visitorName,
      visitorPhoneNumber,
      motorcycle,
      usage,
      kilometers,
      selectedTyreType,
      motorcycleBrand,
      motorcycleYear
    };

    // Track the tire click with usageId
    this.props.actions.trackTireClick(tireInfo, wizardResponses, usageId);
  };

  render() {
    const { classes, catalog } = this.props;
    const {
      motorcycle = '',
      usage = '',
      kilometers = '',
      motorcycleBrand = '',
      motorcycleYear = '',
      userBikeModelToInsert = '',
      visitorName = '',
      visitorPhoneNumber = '',
      currentWizardStep = 0,
      loadingAnswers = false,
      showSuggestedTires = false,
      showLayout = false,
      tiresSuggestedByGPT = null,
      allMotoBrandsAvailable = [],
      motoListForGPT = null,
      userModelLoading = false,
      showUserModelSuccess = false,
      userHasSubmittedModel = false,
      selectedTyreType = '',
      noTiresFound = false,
      limitWizardReached = false,
      gptResponseError = false,
    } = catalog.wizardPage || {};

    const countCompletedSteps = (data, step) => {
      if (step === 0) {
        let count = 0;
        if (data.visitorName?.trim()) count++;
        if (data.visitorPhoneNumber?.trim()) count++;
        return count;
      } else if (step === 1) {
        let count = 0;
        if (data.motorcycleBrand?.trim() && data.motorcycleBrand !== hebrewLables.selectYourMotorcycleBrand) count++;
        if (data.motorcycleYear?.trim() && data.motorcycleYear !== hebrewLables.selectYourMotorcycleYear) count++;
        if (data.selectedTyreType?.trim() && data.selectedTyreType !== hebrewLables.chooseFrontRearOrSet) count++;
        return count;
      } else if (step === 1.5) {
        let count = 0;
        if (data.userBikeModelToInsert?.trim()) count++;
        return count;
      } else if (step === 2) {
        let count = 0;
        if (data.motorcycle?.trim()) count++;
        if (data.usage?.trim() && data.usage !== hebrewLables.selectYourUsage) count++;
        if (data.kilometers?.trim() && data.kilometers !== hebrewLables.averageKilometers) count++;
        return count;
      } else {
        return 0;
      }
    };

    const completedSteps = countCompletedSteps({ motorcycleBrand, motorcycleYear, motorcycle, usage, kilometers, userBikeModelToInsert, selectedTyreType, visitorName, visitorPhoneNumber }, currentWizardStep);
    const totalSteps = currentWizardStep === 0 ? 2 : currentWizardStep === 1 ? 3 : currentWizardStep === 1.5 ? 1 : currentWizardStep === 2 ? 3 : 3;
    const progressValue = (completedSteps / totalSteps) * 100;

    const firstStepComplete = motorcycleBrand?.trim() && motorcycleBrand !== hebrewLables.selectYourMotorcycleBrand && 
                             motorcycleYear?.trim() && motorcycleYear !== hebrewLables.selectYourMotorcycleYear &&
                             selectedTyreType?.trim() && selectedTyreType !== hebrewLables.chooseFrontRearOrSet;
    const customBikeStepComplete = userBikeModelToInsert?.trim();
    const secondStepComplete = motorcycle?.trim() && usage?.trim() && usage !== hebrewLables.selectYourUsage && kilometers?.trim() && kilometers !== hebrewLables.averageKilometers;
    
    const isLoading = loadingAnswers;

    return (
      <Paper className={classes.paper}>
        <div className={classes.containerLayout}>
          {!showLayout && (
            <div className={classes.columnLayoutForm}>
              <div className={classes.containerTextLeft}>
                <Typography className={classes.columnTitle}>
                  {hebrewLables.titleFirstSection}
                </Typography>
                <Typography className={classes.columnTitle}>
                  {hebrewLables.titleSecondSection}
                </Typography>
                <Typography className={classes.columnSubTitle}>
                  {hebrewLables.subtitleFirstSection}
                </Typography>
                <Typography className={classes.columnSubTitle}>
                  {hebrewLables.subtitleSecondSection}
                </Typography>
              </div>
              <div className={classes.containerSelects}>

                {currentWizardStep === 0 && (
                  <>
                    <FormControl component="fieldset" className={classes.formControl}>
                      <TextField
                        id="visitorName"
                        onChange={this.handleTextFieldChange}
                        label={hebrewLables.insertYourName}
                        className={classes.textField}
                        value={visitorName}
                        margin="normal"
                        error={this.state.validationErrors.name}
                        helperText={this.state.validationErrors.name ? hebrewLables.errors.fullNameRequired : ""}
                      />
                    </FormControl>

                    <FormControl component="fieldset" className={classes.formControl}>
                      <TextField
                        id="visitorPhoneNumber"
                        type="tel"
                        onChange={this.handleTextFieldChange}
                        label={hebrewLables.insertYourPhoneNumber}
                        className={classes.textField}
                        value={visitorPhoneNumber}
                        margin="normal"
                        error={this.state.validationErrors.phone}
                        helperText={this.state.validationErrors.phone ? hebrewLables.errors.phoneFormat : ""}
                        placeholder="05XXXXXXXX"
                      />
                    </FormControl>
                  </>
                )}

                {currentWizardStep === 1 && (
                  <>
                    <div className={classes.containerPickMoto}>
                      <Typography className={classes.textTitleForm}>
                        {hebrewLables.pickYourMotorcycleBrand}
                      </Typography>
                      <Autocomplete
                        options={allMotoBrandsAvailable || []}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          return option || '';
                        }}
                        value={motorcycleBrand || null}
                        onChange={(event, newValue) => {
                          this.setWizardUserAnswer('motorcycleBrand', newValue || '');
                        }}
                        classes={{
                          listbox: classes.autocompleteListbox,
                          paper: classes.autocompletePaper,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className={classes.formKilometers}
                            variant="outlined"
                            placeholder={hebrewLables.selectYourMotorcycleBrand}
                          />
                        )}
                      />
                    </div>

                    <div className={classes.containerPickMoto}>
                      <Typography className={classes.textTitleForm}>
                        {hebrewLables.selectYourMotorcycleYear}
                      </Typography>
                      <Autocomplete
                        options={MOTORCYCLE_YEARS || []}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          return option || '';
                        }}
                        value={motorcycleYear || null}
                        onChange={(event, newValue) => {
                          this.setWizardUserAnswer('motorcycleYear', newValue || '');
                        }}
                        classes={{
                          listbox: classes.autocompleteListbox,
                          paper: classes.autocompletePaper,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className={classes.formKilometers}
                            variant="outlined"
                            placeholder={hebrewLables.selectYourMotorcycleYear}
                          />
                        )}
                      />
                    </div>

                    <div className={classes.containerPickMoto}>
                      <Typography className={classes.textTitleForm}>
                        {hebrewLables.doYouWantFrontRearOrSet}
                      </Typography>
                      <FormControl variant="outlined" className={`${classes.formKilometers} ${classes.selectFocusRemoval}`}>
                        <Select
                          value={selectedTyreType || ''}
                          onChange={(event) => this.setWizardUserAnswer('selectedTyreType', event.target.value)}
                          displayEmpty
                          style={{ height: '2.4rem' }}
                        >
                          <MenuItem value="" disabled>
                            {hebrewLables.chooseFrontRearOrSet}
                          </MenuItem>
                          {TYRE_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </>
                )}

                {currentWizardStep === 1.5 && (
                  <div className={classes.containerPickMoto}>
                    <Typography className={classes.textTitleForm}>
                      {hebrewLables.insertYourMotorcycleModelInfo}
                    </Typography>
                    <TextField
                      className={classes.formKilometers}
                      variant="outlined"
                      placeholder={hebrewLables.enterYourMotorcycleModel}
                      value={this.props.catalog.wizardPage.userBikeModelToInsert}
                      onChange={this.handleCustomBikeModelChange}
                    />
                  </div>
                )}

                {currentWizardStep === 2 && (
                  <>
                    {motoListForGPT && motoListForGPT.length > 0 ? (
                      <div className={classes.containerPickMoto}>
                        <Typography className={classes.textTitleForm}>
                          {hebrewLables.pickYourMotorcycle}
                        </Typography>
                        <Autocomplete
                          options={motoListForGPT || []}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            return option.model || '';
                          }}
                          getOptionSelected={(option, value) => {
                            return option.id === value.id;
                          }}
                          value={motoListForGPT.find(moto => moto.id === motorcycle) || null}
                          onChange={(event, newValue) => {
                            this.setWizardUserAnswer('motorcycle', newValue ? newValue.id : '');
                          }}
                          classes={{
                            listbox: classes.autocompleteListbox,
                            paper: classes.autocompletePaper,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              className={classes.formKilometers}
                              variant="outlined"
                              placeholder={hebrewLables.enterYourMotorcycleModel}
                            />
                          )}
                        />
                      </div>
                    ) : (
                      <div className={classes.containerPickMoto}>
                        <Typography className={classes.textTitleForm}>
                          {hebrewLables.insertYourMotorcycleModel}
                        </Typography>
                        <TextField
                          className={classes.formKilometers}
                          variant="outlined"
                          placeholder={hebrewLables.enterYourMotorcycleModel}
                          value={motorcycle}
                          onChange={(event) => this.setWizardUserAnswer('motorcycle', event.target.value)}
                        />
                      </div>
                    )}

                    <div className={classes.containerPickMoto}>
                      <Typography className={classes.textTitleForm}>
                        {hebrewLables.howDoYouRide}
                      </Typography>
                      <FormControl variant="outlined" className={`${classes.formKilometers} ${classes.selectFocusRemoval}`}>
                        <Select
                          value={usage || ''}
                          onChange={(event) => this.setWizardUserAnswer('usage', event.target.value)}
                          displayEmpty
                          style={{ height: '2.4rem' }}
                        >
                          <MenuItem value="" disabled>
                            {hebrewLables.selectYourUsage}
                          </MenuItem>
                          {USAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    <div className={classes.containerPickMoto}>
                      <Typography className={classes.textTitleForm}>
                        {hebrewLables.yourMonthlyKilometers}
                      </Typography>
                      <FormControl variant="outlined" className={`${classes.formKilometers} ${classes.selectFocusRemoval}`}>
                        <Select
                          value={kilometers || ''}
                          onChange={(event) => this.setWizardUserAnswer('kilometers', event.target.value)}
                          displayEmpty
                          style={{ height: '2.4rem' }}
                        >
                          <MenuItem value="" disabled>
                            {hebrewLables.averageKilometers}
                          </MenuItem>
                          {KILOMETERS_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </>
                )}

                {currentWizardStep !== 1.5 && (
                  <div className={classes.containerProgress} key={`progress-${currentWizardStep}`}>
                    <div className={classes.containerTextProgress}>
                      <Typography>{hebrewLables.completedQuestions}</Typography>
                      <Typography key={`steps-${completedSteps}-${totalSteps}`}>{completedSteps} / {totalSteps}</Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={progressValue}
                      classes={{
                        root: classes.rootProgress,
                        bar: classes.barProgress,
                      }}
                    />
                  </div>
                )}

                {currentWizardStep === 2 && secondStepComplete && (
                  <div className={classes.containerTableSummary}>
                    <TableContainer component={Paper} className={classes.rootTable}>
                      <Typography variant="subtitle1" className={classes.header}>
                        {hebrewLables.summary}
                      </Typography>
                      <Table className={classes.table} aria-label="summary table">
                        <TableBody>
                          <TableRow>
                            <TableCell className={classes.iconCell}>
                              <DirectionsBikeIcon fontSize="small" />
                              {hebrewLables.brand}
                            </TableCell>
                            <TableCell>{motorcycleBrand}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.iconCell}>
                              <DateRangeIcon fontSize="small" />
                              {hebrewLables.year}
                            </TableCell>
                            <TableCell>{motorcycleYear}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.iconCell}>
                              <DirectionsBikeIcon fontSize="small" />
                              {hebrewLables.bike}
                            </TableCell>
                            <TableCell>{motoListForGPT?.find(moto => moto.id === motorcycle)?.model || motorcycle}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.iconCell}>
                              <DateRangeIcon fontSize="small" />
                              {hebrewLables.usage}
                            </TableCell>
                            <TableCell>{usage}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.iconCell}>
                              <RoomIcon fontSize="small" />
                              {hebrewLables.km}
                            </TableCell>
                            <TableCell>{kilometers}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
  
                {currentWizardStep === 1 && Array.isArray(motoListForGPT) && motoListForGPT.length === 0 && !userHasSubmittedModel && (
                  <div className={classes.containerNoMotoAvailable}>
                    <Button
                      className={classes.buttonNoMotoAvailable}
                      onClick={this.handleShowCustomBikeInput}
                    >
                      {hebrewLables.yourBikeModelNotInCatalog}
                    </Button>
                  </div>
                )}
                
                <div className={classes.containerButtonGenerate}>
                  {currentWizardStep === 0 ? (
                    visitorName?.trim() && visitorPhoneNumber?.trim() && !this.state.validationErrors.name && !this.state.validationErrors.phone && !isLoading ? (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.buttonContinue}
                        onClick={() => this.props.actions.setWizardStep(1)}
                        id="target-scroll"
                      >
                        {hebrewLables.continue}
                      </Button>
                    ) : (
                      <Tooltip
                        title={hebrewLables.pleaseCompleteAllQuestionsBeforeProceeding}
                        classes={{ tooltip: classes.tooltipContent }}
                      >
                        <span style={{ width: "100%", height: "3rem" }}>
                          <Button
                            variant="contained"
                            disabled
                            className={classes.buttonGenerateDisabled}
                            id="target-scroll"
                          >
                            {hebrewLables.continue}
                          </Button>
                        </span>
                      </Tooltip>
                    )
                  ) : currentWizardStep === 1 ? (
                    <div className={classes.containerButtonGenerateBack}>
                      <Button
                        variant="contained"
                        className={classes.buttonStepBack}
                        onClick={this.handleBackToStep0}
                        id="target-scroll"
                      >
                        {hebrewLables.back}
                      </Button>
                      {firstStepComplete && !isLoading ? (
                                                  <Button
                            variant="contained"
                            color="primary"
                            className={classes.buttonContinue}
                            onClick={this.handleContinueClick}
                            id="target-scroll"
                          >
                            {hebrewLables.continue}
                          </Button>
                      ) : (
                        <Tooltip
                          title={hebrewLables.pleaseCompleteAllQuestionsBeforeProceeding}
                          classes={{ tooltip: classes.tooltipContent }}
                        >
                          <span style={{ width: "100%", height: "3rem" }}>
                            <Button
                              variant="contained"
                              disabled
                              className={classes.buttonGenerateDisabled}
                              id="target-scroll"
                            >
                              {hebrewLables.continue}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  ) : currentWizardStep === 1.5 && customBikeStepComplete ? (
                    <div className={classes.containerButtonGenerateBack}>
                      <Button
                        variant="contained"
                        className={classes.buttonStepBack}
                        onClick={this.handleBackToStep1}
                        id="target-scroll"
                      >
                        {hebrewLables.back}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.buttonGenerate}
                        onClick={this.handleSendUserBikeToAdd}
                        id="target-scroll"
                      >
                        {hebrewLables.sendUs}
                      </Button>
                    </div>
                  ) : currentWizardStep === 1.5 ? (
                    <div className={classes.containerButtonGenerateBack}>
                      <Button
                        variant="contained"
                        className={classes.buttonStepBack}
                        onClick={this.handleBackToStep1}
                        id="target-scroll"
                      >
                        {hebrewLables.back}
                      </Button>
                      <Tooltip
                        title={hebrewLables.pleaseEnterYourMotorcycleModelBeforeSending}
                        classes={{ tooltip: classes.tooltipContent }}
                      >
                        <span style={{ width: "100%", height: "3rem" }}>
                          <Button
                            variant="contained"
                            disabled
                            className={classes.buttonGenerateDisabled}
                            id="target-scroll"
                          >
                            {hebrewLables.sendUs}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  ) : currentWizardStep === 2 && secondStepComplete ? (
                    <div className={classes.containerButtonGenerateBack}>
                      <Button
                        variant="contained"
                        className={classes.buttonStepBack}
                        onClick={this.handleBackToStep1}
                        id="target-scroll"
                      >
                        {hebrewLables.back}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.buttonGenerate}
                        onClick={this.handleGenerateClick}
                        id="target-scroll"
                      >
                        {hebrewLables.generate}
                      </Button>
                    </div>
                  ) : (
                    <div className={classes.containerButtonGenerateBack}>
                      <Button
                        variant="contained"
                        className={classes.buttonStepBack}
                        onClick={this.handleResetWizard}
                        id="target-scroll"
                      >
                        {hebrewLables.back}
                      </Button>
                      <Tooltip
                        title={hebrewLables.pleaseCompleteAllQuestionsBeforeGenerating}
                        classes={{ tooltip: classes.tooltipContent }}
                      >
                        <span style={{ width: "100%", height: "3rem" }}>
                          <Button
                            variant="contained"
                            disabled
                            className={classes.buttonGenerateDisabled}
                            id="target-scroll"
                          >
                            {hebrewLables.generate}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {showLayout && (
            <div className={classes.columnLayoutResult}>
              <div>
                <Typography className={classes.columnTitle}>{hebrewLables.topTires}</Typography>
              </div>
              <div>
                <Typography className={classes.columnSubTitle}>
                  • {motorcycleBrand} • {motorcycleYear} • {motoListForGPT?.find(moto => moto.id === motorcycle)?.model || motorcycle} • {kilometers}Km • {usage} • {selectedTyreType}
                </Typography>
              </div>

              {loadingAnswers && (
                <div className={classes.inlineLoadingContainer}>
                  {this.renderLottieAnimation(classes)}
                  <Typography className={classes.loadingText}>
                    {hebrewLables.weAreSearchingBestTiresRecommendation}
                  </Typography>
                </div>
              )}

              {!loadingAnswers && noTiresFound && (
                <div className={classes.noTiresFoundContainer}>
                  <SearchIcon className={classes.noTiresFoundIcon} />
                  <Typography className={classes.noTiresFoundTitle}>
                    {hebrewLables.noTiresFound}
                  </Typography>
                  <Typography className={classes.noTiresFoundMessage}>
                    {hebrewLables.noTiresFoundMessage}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.noTiresFoundButton}
                    onClick={this.handleResetWizard}
                  >
                    {hebrewLables.tryAgain}
                  </Button>
                </div>
              )}

              {!loadingAnswers && limitWizardReached && (
                <div className={classes.limitReachedContainer}>
                  <BlockIcon className={classes.limitReachedIcon} />
                  <Typography className={classes.limitReachedTitle}>
                    {hebrewLables.serviceTemporarilyUnavailable}
                  </Typography>
                  <Typography className={classes.limitReachedMessage}>
                    {hebrewLables.limitReachedMessage}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.limitReachedButton}
                    onClick={this.handleResetWizard}
                  >
                    {hebrewLables.backToStart}
                  </Button>
                </div>
              )}

              {!loadingAnswers && gptResponseError && (
                <div className={classes.gptErrorContainer}>
                  <ErrorIcon className={classes.gptErrorIcon} />
                  <Typography className={classes.gptErrorTitle}>
                    {hebrewLables.aiResponseError}
                  </Typography>
                  <Typography className={classes.gptErrorMessage}>
                    {hebrewLables.errorToGenerateAnswers}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.gptErrorButton}
                    onClick={this.handleResetWizard}
                  >
                    {hebrewLables.tryAgain}
                  </Button>
                </div>
              )}

              {!loadingAnswers && showSuggestedTires && (
                <div className={classes.containerSuggestedTire}>
                  {tiresSuggestedByGPT.map(t => (
                    <SuggestedTireCard tireInfo={t} key={t.ean} onTireClick={this.handleTireClick} />
                  ))}
                </div>
              )}

              {!loadingAnswers && showSuggestedTires && (
                <div className={classes.containerBackButton}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonBack}
                    onClick={this.handleResetWizard}
                  >
                    {hebrewLables.back}
                  </Button>

                </div>
              )}
            </div>
          )}
          
        </div>



        {userModelLoading && (
          <div className={classes.inlineLoadingContainer}>
            {this.renderLottieAnimation(classes)}
            <Typography className={classes.loadingText}>
              {hebrewLables.weAreReceivingYourMotorcycleModel}
            </Typography>
          </div>
        )}

        {showUserModelSuccess && (
          <div className={classes.successPanel}>
            <div className={classes.successPanelContent}>
              <CheckCircleIcon className={classes.successIcon} />
              <Typography className={classes.successTitle}>
                {hebrewLables.success}
              </Typography>
              <Typography className={classes.successMessage}>
                {hebrewLables.yourMotorcycleModelHasBeenSent}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                className={classes.successButton}
                onClick={this.handleCloseSuccessPanel}
              >
                {hebrewLables.continue}
              </Button>
            </div>
          </div>
        )}
      </Paper>
    );
  }
}

TireWizardPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
  match: PropTypes.object,
};

export default withStyles(styles)(TireWizardPage);
